import React, { useEffect, useRef, useState } from "react";
import useEditLoad from "../../hooks/useEditLoad";
import EditLoadGate from "../../components/editLoadGate";
import axios from "axios";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import { BsFiletypeCsv } from "react-icons/bs";

import styles from "./genre.module.scss";
import GenreCard from "./genreCard";
import { uploadMediaFile } from "../../utills/fileUpload";
import Spinner from "../../components/spinner";
import { useLanguage } from "../../context/languageContext";
import LanguageSelector from "../../components/languageSelector";
import PreviewImage from "../../components/image/previewImage";
import GenerateCSV from "../../components/popup/generateCSV";
import Button from "../../components/button";
import { convertToCloudFrontUrl } from "../../helper";
import { Can } from "../../context/permissionContext";
import { confirmCascadeDelete } from "../../helper/confirmCascadeDelete";
import { useAlreadyExists } from "../../components/confirmDialog";

function Genre() {
  const { t } = useTranslation();
  const { languages, selectedLang, setSelectedLang, adminInfo, initialLangRef, fallbackLang, isShowCSVModalVisible, setIsShowCSVModalVisible, handleCloseCSV, handleGenerateCSV } = useLanguage();
  const showAlreadyExists = useAlreadyExists();

  const [loading, setLoading] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);

  const [allGenresData, setAllGenresData] = useState([]);
  const [editGenresData, setEditGenresData] = useState([]);

  const [editMediaError, setEditMediaError] = useState("");
  const [errors, setErrors] = useState({});

  const [isAddGenreModalVisible, setIsAddGenreModalVisible] = useState(false);
  const [isEditGenreModalVisible, setIsEditGenreModalVisible] = useState(false);
  // Edit shows only the genre that was opened — see hooks/useEditLoad.
  const load = useEditLoad();
  const [requestedGenreId, setRequestedGenreId] = useState(null);
  const editReady = Boolean(isEditGenreModalVisible && load.isLoadedFor(requestedGenreId));

  const createInitialLangValues = (lang) => ({
    langCode: lang,
    name: ""
  });

  const createInitialTranslationsArr = () => languages.map((lang) => createInitialLangValues(lang));

  const initialLangValues = {
    translations: createInitialTranslationsArr(),
    type: 'NonFiction',
    image: {},
  };

  const [values, setValues] = useState(initialLangValues);
  const [activeTab, setActiveTab] = useState(1);

  const hasGenresFetched = useRef(false);

  useEffect(() => {
    if (adminInfo && adminInfo.token && !hasGenresFetched.current) {
      fetchAllGenresData();
      document.title = "Penspace - Genre";
      hasGenresFetched.current = true;
    }
  }, [adminInfo]);

  useEffect(() => {
    if ((isAddGenreModalVisible || isEditGenreModalVisible) && fallbackLang) {
      setSelectedLang(fallbackLang);
    }
  }, [isAddGenreModalVisible, isEditGenreModalVisible]);

  useEffect(() => {
    if (editGenresData && editGenresData.translations && !initialLangRef.current) {
      const mergedTranslations = languages.map((lang) => {
        return (
          editGenresData.translations.find((t) => t.langCode === lang) || {
            langCode: lang,
            name: "",
          }
        );
      });

      // Normalize genre image: convert S3 URLs to CloudFront URLs
      const normalizeGenreImage = (image) => {
        if (!image || !image.uploadedImageUrl) {
          return image || {};
        }
        return {
          ...image,
          uploadedImageUrl: convertToCloudFrontUrl(image.uploadedImageUrl)
        };
      };

      setValues({
        translations: mergedTranslations,
        image: normalizeGenreImage(editGenresData.image),
        type: editGenresData.type || "NonFiction",
      });

      initialLangRef.current = true;
      load.loaded(editGenresData);
    }
  }, [editGenresData, languages]);

  useEffect(() => {
    if (!isEditGenreModalVisible) {
      load.reset();
      setRequestedGenreId(null);
    }
    // `load.reset` is stable; listing `load` would re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditGenreModalVisible]);

  const handleGetTranslationByLang = (langCode) => {
    return values.translations?.find((t) => t.langCode === langCode) || {};
  };

  const handleSetActiveTab = (tab) => {
    setActiveTab(tab);
  };

  const handleSelectLanguage = (language) => {
    setSelectedLang(language);

    setValues((prevValues) => {
      const exists = prevValues.translations?.some((t) => t.langCode === language);
      if (exists) return { ...prevValues };

      return {
        ...prevValues,
        translations: [...prevValues.translations, createInitialLangValues(language)],
      };
    });
  };

  const handleAddValues = (field, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [field]: value,
    }));
  };

  const handleUpdateTranslationField = (fieldName, value, langCode) => {
    setValues((prevValues) => {
      const updatedTranslations = prevValues.translations?.map((translation) =>
        translation.langCode === langCode
          ? { ...translation, [fieldName]: value }
          : translation
      );

      return {
        ...prevValues,
        translations: updatedTranslations,
      };
    });
  };

  const handleUploadMedia = async (event) => {
    const result = await uploadMediaFile(event, true);

    if (typeof result === "string") {
      setEditMediaError(result);
    } else if (typeof result === "object" && result.length > 0) {
      setValues((prevValues) => ({
        ...prevValues,
        image: result[0]?.data,
      }));

      setEditMediaError("");
    }
  };

  const handleAddCloseModal = () => {
    setIsAddGenreModalVisible(false);
    setValues(initialLangValues);
    setErrors({});
  };

  const handleEditCloseModal = () => {
    setIsEditGenreModalVisible(false);
    setValues(initialLangValues);
    setErrors({});
  };

  const validateGenreValues = (value) => {
    let newErrors = {};
    const translation = value.translations.find((lang) => lang.langCode === selectedLang);

    if (!translation.name || translation.name.trim() === "") {
      newErrors.name = "Please add a name of the genre";
    }
    if (!value.type || value.type.trim() === "") {
      newErrors.type = "Please add the type";
    }
    if (!value.image || Object.keys(value.image).length === 0 || !value.image.uploadedImageUrl) {
      newErrors.image = "Please upload an image";
    };

    return newErrors;
  };

  const handleAddNewGenre = async () => {
    let newErrors = validateGenreValues(values);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSubmit(true);

      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/genre/add`, { values }, { headers });

      if (response.status) {
        setIsAddGenreModalVisible(false);
        setErrors({});
        fetchAllGenresData();
        toast.success(response?.message);
        setValues(initialLangValues);
        setIsSubmit(false);
      }
    } catch (error) {
      console.error("Error adding genre:", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error;
      if (!showAlreadyExists(error)) toast.error(errorMessage);
    } finally {
      setIsSubmit(false);
    }
  };

  const fetchAllGenresData = async () => {
    setLoading(true);

    try {
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/genre/getall`, { headers });

      if (response.status) {
        // Normalize genre images: convert S3 URLs to CloudFront URLs immediately after API fetch
        const normalizeGenreImage = (image) => {
          if (!image || !image.uploadedImageUrl) {
            return image;
          }
          return {
            ...image,
            uploadedImageUrl: convertToCloudFrontUrl(image.uploadedImageUrl)
          };
        };

        const normalizedData = {
          ...response.data,
          allGenres: response.data.allGenres?.map(genre => ({
            ...genre,
            image: normalizeGenreImage(genre.image)
          })) || [],
          fictionGenres: response.data.fictionGenres?.map(genre => ({
            ...genre,
            image: normalizeGenreImage(genre.image)
          })) || [],
          nonFictionGenres: response.data.nonFictionGenres?.map(genre => ({
            ...genre,
            image: normalizeGenreImage(genre.image)
          })) || []
        };

        setAllGenresData(normalizedData);
      } else {
        setAllGenresData([]);
      }
    } catch (error) {
      console.error("fetchAllGenresData catch error::", error);
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  };

  const fetchSpecificGenreData = async (categoryId) => {
    const id = String(categoryId);
    load.begin(id);
    setRequestedGenreId(id);
    setIsEditGenreModalVisible(true);
    initialLangRef.current = false;

    try {
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/genre/get/specific/${id}`, { headers });

      if (response?.status && load.accepts(id, response.data)) {
        setEditGenresData(response.data);
      } else {
        load.fail(id, response?.message || "");
      }
    } catch (error) {
      console.error("fetchSpecificGenreData error:", error);
      load.fail(id, error);
    }
  };

  const updateGenreData = async (categoryId) => {
    const newErrors = validateGenreValues(values);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSubmit(true);

      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const response = await axios.put(`${process.env.REACT_APP_API_ROOT}/api/admin/genre/update/${categoryId}`, { values }, { headers });

      if (response.status) {
        fetchAllGenresData();
        setIsEditGenreModalVisible(false);
        setValues(initialLangValues);
        toast.success(response?.message);
        setErrors({});
        setIsSubmit(false);
      }
    } catch (error) {
      console.error("updateGenreData catch error:::", error)
      const errorMessage = error.response?.data?.message || error.response?.data?.error;
      if (!showAlreadyExists(error)) toast.error(errorMessage);
    } finally {
      setIsSubmit(false);
    }
  };

  const handleRemoveGenreData = async (categoryId) => {
    const headers = { Authorization: `Bearer ${adminInfo.token}` };
    // The trash icon used to delete on the spot. A genre is copied onto books,
    // summaries, authors, series, journeys, recommenders, tags and posts, and
    // deleting it removes it from all of them — so ask first.
    if (!(await confirmCascadeDelete({ source: "genre", ids: [categoryId], what: "this genre", headers }))) return;

    try {
      // Same shape as the home-page delete: `headers` sat in the body slot, so
      // the config slot was empty and no token was sent.
      const { data: response } = await axios.put(
        `${process.env.REACT_APP_API_ROOT}/api/admin/genre/delete/${categoryId}`,
        {},
        { headers }
      );

      if (response.status) {
        fetchAllGenresData();
        setValues(initialLangValues);
        toast.success(response.message || "Genre deleted successfully.");
      } else {
        toast.error(response.message || "Could not delete this genre.");
      }
    } catch (error) {
      console.error("handleRemoveGenreData catch error::", error);
      toast.error(error.response?.data?.error || error.response?.data?.message || "Could not delete this genre.");
    }
  };


  return (
    <React.Fragment>
      {loading ? (
        <Spinner />
      ) : (
        <div className={styles.wrapper}>
          <div className="heading">
            <h4>{allGenresData && allGenresData.length > 1 ? `Genres (${allGenresData?.length})` : "Genre"}</h4>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-dark" onClick={() => setIsShowCSVModalVisible(true)}>Generate CSV <BsFiletypeCsv /></button>
              <Can feature="genres" action="create"><button type="button" className="btn btn-dark" onClick={() => setIsAddGenreModalVisible(true)}>Add Genre</button></Can>
            </div>
          </div>
          <div className={styles.tabs}>
            <div className="d-flex gap-1">
              <p
                className={activeTab === 1 ? styles.activeTab : styles.tab} onClick={() => handleSetActiveTab(1)}>
                All ({(allGenresData && allGenresData.allGenres && allGenresData.allGenres.length) || 0})
              </p>
              <p
                className={activeTab === 2 ? styles.activeTab : styles.tab} onClick={() => handleSetActiveTab(2)}>
                Fiction ({(allGenresData && allGenresData.fictionGenres && allGenresData.fictionGenres.length) || 0})
              </p>
              <p
                className={activeTab === 3 ? styles.activeTab : styles.tab} onClick={() => handleSetActiveTab(3)}>
                NonFiction ({(allGenresData && allGenresData.nonFictionGenres && allGenresData.nonFictionGenres.length) || 0})
              </p>
            </div>
          </div>
          {allGenresData && allGenresData.allGenres && allGenresData.allGenres.length > 0 && (
            <div className={styles.genreContainer}>
              <Row className="g-1">
                {activeTab === 1 && allGenresData.allGenres.map((genre, index) => (
                  <Col key={index} xs={12} sm={6} md={4} lg={2} className="g-3">
                    <GenreCard
                      genre={genre}
                      onEditSuccess={fetchSpecificGenreData}
                      onDeleteSuccess={handleRemoveGenreData}
                    />
                  </Col>
                ))}
              </Row>

              <Row className="g-0">
                {activeTab === 2 && allGenresData.fictionGenres && allGenresData.fictionGenres.length > 0 &&
                  allGenresData.fictionGenres.map((genre, index) => (
                    <Col key={index} xs={12} sm={6} md={4} lg={2} className="g-3">
                      <GenreCard
                        genre={genre}
                        onEditSuccess={fetchSpecificGenreData}
                        onDeleteSuccess={handleRemoveGenreData}
                      />
                    </Col>
                  ))}
              </Row>

              <Row className="g-0">
                {activeTab === 3 && allGenresData.nonFictionGenres && allGenresData.nonFictionGenres.length > 0 &&
                  allGenresData.nonFictionGenres.map((genre, index) => (
                    <Col key={index} xs={12} sm={6} md={4} lg={2} className="g-3">
                      <GenreCard
                        genre={genre}
                        onEditSuccess={fetchSpecificGenreData}
                        onDeleteSuccess={handleRemoveGenreData}
                      />
                    </Col>
                  ))}
              </Row>
            </div>
          )}

          <div>
            <Modal
              isOpen={isAddGenreModalVisible}
              onRequestClose={handleAddCloseModal}
              className="modal_body_sm"
              overlayClassName="modal_overlay"
            >
              <div className="box_wrapper">
                <div className="d-flex justify-content-between fw-bold mb-3">
                  <h4 className="text-center">Add Genre</h4>
                  {/* <LanguageSelector onSelectLanguage={handleSelectLanguage}/> */}
                </div>
                <div className="modal_content_sm">
                  <div className="mb-3 mt-2">
                    <div className="d-flex gap-3">
                      <label className="label">{t("general.category")}:</label>
                      {["Fiction", "NonFiction"].map((option) => (
                        <div key={option} className="d-flex align-items-center gap-2">
                          <input
                            type="radio"
                            name="type"
                            value={option}
                            checked={values.type === option}
                            onChange={(e) => handleAddValues(e.target.name, e.target.value)}
                            style={{ width: "20px", height: "20px" }}
                          />
                          <label className="form-check-label">{t(`general.${option}`)}</label>
                        </div>
                      ))}
                    </div>
                    {errors && errors.type && <p className="error-message d-block">{errors.type}</p>}
                  </div>
                  <div className="mb-3">
                    <label className="label">{t("general.genre")}:</label>
                    <input
                      className="form_control"
                      placeholder={`${t("general.enter")} ${t("general.genre")}...`}
                      type="text"
                      name="name"
                      value={handleGetTranslationByLang(selectedLang).name || ''}
                      onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                    />
                    {errors && errors.name && <p className="error-message d-block">{errors.name}</p>}
                  </div>
                  <div className="mb-3">
                    <PreviewImage
                      label={t("general.coverImage")}
                      image={values.image?.uploadedImageUrl}
                      mediaError={editMediaError}
                      error={errors?.image}
                      uploadMedia={(e) => handleUploadMedia(e)}
                    />
                  </div>
                </div>
                <div className="d-flex gap-2 justify-content-end mt-3">
                  <button type="button" className="btn btn-outline-secondary" onClick={handleAddCloseModal} disabled={isSubmit}>Cancel</button>
                  <Button onClick={handleAddNewGenre} disabled={isSubmit} isSubmit={isSubmit} label="Submitting ..." text="Submit" />
                </div>
              </div>
            </Modal>
            <Modal
              isOpen={isEditGenreModalVisible}
              onRequestClose={handleEditCloseModal}
              className="modal_body_sm"
              overlayClassName="modal_overlay"
            >
              <div className="box_wrapper">
                <div className="d-flex justify-content-between fw-bold mb-3">
                  <h4>Edit Genre</h4>
                  <LanguageSelector onSelectLanguage={handleSelectLanguage} />
                </div>
                <EditLoadGate ready={editReady} error={load.loadError}>
                <div className="modal_content_sm">
                  <div className="mb-3 mt-2">
                    <div className="d-flex gap-3">
                      <label className="label">{t("general.category")}:</label>
                      {["Fiction", "NonFiction"].map((option) => (
                        <div key={option} className="d-flex align-items-center gap-2">
                          <input
                            type="radio"
                            name="type"
                            value={option}
                            checked={values.type === option}
                            onChange={(e) => handleAddValues(e.target.name, e.target.value)}
                            style={{ width: "20px", height: "20px" }}
                          />
                          <label className="form-check-label">{t(`general.${option}`)}</label>
                        </div>
                      ))}
                    </div>
                    {errors && errors.type && <p className="error-message d-block">{errors.type}</p>}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="articleId" className="label">{t("general.genre")}:</label>
                    <input
                      className="form_control"
                      placeholder="Enter genre name..."
                      type="text"
                      name="name"
                      value={handleGetTranslationByLang(selectedLang).name || ""}
                      onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                    />
                    {errors && errors.name && <p className="error-message d-block">{errors.name}</p>}
                  </div>
                  <div className="mb-3">
                    <PreviewImage
                      label={t("general.coverImage")}
                      image={values.image?.uploadedImageUrl}
                      mediaError={editMediaError}
                      error={errors?.image}
                      uploadMedia={(e) => handleUploadMedia(e)}
                    />
                  </div>
                </div>
                </EditLoadGate>
                <div className="d-flex gap-2 justify-content-end mt-3">
                  <button type="button" className="btn btn-outline-secondary" onClick={handleEditCloseModal} disabled={isSubmit}>Cancel</button>
                  <Button onClick={() => editReady && updateGenreData(editGenresData._id)} disabled={isSubmit || !editReady} isSubmit={isSubmit} label="Updating ..." text="Update" />
                </div>
              </div>
            </Modal>

            <GenerateCSV
              title="genres"
              isShowCSVModalVisible={isShowCSVModalVisible}
              handleCloseCSV={handleCloseCSV}
              handleGenerateCSV={() => handleGenerateCSV("genres")}
            />
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export default Genre;
